import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Search, ArrowLeft, TrendingUp, TrendingDown, Minus, Clock, CalendarDays, Trophy, Target, Loader2, ChevronDown, ChevronUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL.replace(/^http:\/\//i, 'https://');

const SPORTS = ['Alle', 'Soccer', 'Basketball', 'Ice Hockey', 'American Football', 'Baseball', 'Handball', 'Rugby', 'Cricket', 'Australian Football', 'Motorsport', 'Volleyball', 'ESports'];

const MatchSearchPage = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [sport, setSport] = useState('Alle');
  const [tab, setTab] = useState('upcoming');
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedMatch, setExpandedMatch] = useState(null);
  const token = localStorage.getItem('token');

  const searchMatches = async (searchQuery, searchSport, searchTab) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('query', searchQuery);
      if (searchSport && searchSport !== 'Alle') params.append('sport', searchSport);
      params.append('limit', '40');

      const endpoint = searchTab === 'upcoming' ? 'upcoming' : 'past';
      const res = await fetch(`${API_URL}/api/match-search/${endpoint}?${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const text = await res.text();
      try {
        const data = JSON.parse(text);
        setMatches(data.matches || []);
      } catch { setMatches([]); }
    } catch {
      toast.error('Fehler bei der Suche');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    searchMatches('', '', tab);
  }, [tab]);

  const handleSearch = (e) => {
    e.preventDefault();
    searchMatches(query, sport, tab);
  };

  const resultIcon = (result) => {
    if (result === 'home_win') return <TrendingUp className="w-4 h-4 text-green-400" />;
    if (result === 'away_win') return <TrendingDown className="w-4 h-4 text-red-400" />;
    return <Minus className="w-4 h-4 text-yellow-400" />;
  };

  const predLabel = (pred) => {
    const labels = {
      home_win: 'Heimsieg', away_win: 'Auswaertssieg', draw: 'Unentschieden',
      over_2_5: 'Ueber 2.5', under_2_5: 'Unter 2.5', both_teams_score: 'Beide treffen',
    };
    return labels[pred] || pred?.replace(/_/g, ' ') || '-';
  };

  const confColor = (conf) => {
    if (conf >= 75) return 'text-green-400';
    if (conf >= 55) return 'text-yellow-400';
    return 'text-red-400';
  };

  return (
    <div className="min-h-screen bg-[#121212]">
      <header className="bg-[#1E1E1E] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-4">
          <button onClick={() => navigate('/dashboard')} className="text-gray-400 hover:text-white" data-testid="search-back-btn">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <Search className="w-5 h-5 text-[#00BFA6]" />
          <h1 className="text-xl font-bold text-white" data-testid="search-title">Match-Suche</h1>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">
        {/* Search Bar */}
        <form onSubmit={handleSearch} className="flex gap-2">
          <Input
            data-testid="search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Team, Liga oder Sportart suchen..."
            className="bg-[#1E1E1E] border-white/10 text-white placeholder-gray-500 flex-1"
          />
          <Button data-testid="search-btn" type="submit" className="bg-[#00BFA6] hover:bg-[#009E8A] text-black font-bold px-6">
            <Search className="w-4 h-4 mr-2" /> Suchen
          </Button>
        </form>

        {/* Sport Filter */}
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {SPORTS.map((s) => (
            <button
              key={s}
              data-testid={`filter-sport-${s.toLowerCase().replace(/\s/g, '-')}`}
              onClick={() => { setSport(s); searchMatches(query, s, tab); }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                sport === s ? 'bg-[#00BFA6] text-black' : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-[#1E1E1E] rounded-lg p-1">
          <button
            data-testid="tab-upcoming"
            onClick={() => setTab('upcoming')}
            className={`flex-1 py-2.5 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
              tab === 'upcoming' ? 'bg-[#00BFA6] text-black' : 'text-gray-400 hover:text-white'
            }`}
          >
            <Clock className="w-4 h-4" /> Kommende Spiele
          </button>
          <button
            data-testid="tab-past"
            onClick={() => setTab('past')}
            className={`flex-1 py-2.5 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
              tab === 'past' ? 'bg-[#00BFA6] text-black' : 'text-gray-400 hover:text-white'
            }`}
          >
            <CalendarDays className="w-4 h-4" /> Vergangene Spiele
          </button>
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#00BFA6] animate-spin" />
          </div>
        ) : matches.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <Search className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>Keine Spiele gefunden. Probiere eine andere Suche.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {matches.map((match, idx) => {
              const pred = match.ai_prediction;
              const isExpanded = expandedMatch === idx;
              const isPast = match.type === 'past';

              return (
                <Card key={idx} data-testid={`match-card-${idx}`} className="bg-[#1E1E1E] border-white/5 hover:border-white/10 transition-all cursor-pointer" onClick={() => setExpandedMatch(isExpanded ? null : idx)}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                          <span className="bg-white/5 px-2 py-0.5 rounded">{match.sport}</span>
                          <span>{match.league}</span>
                          <span>{match.match_date}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-white font-semibold text-sm truncate">{match.team_home}</span>
                          {isPast && match.score ? (
                            <span className="text-[#00BFA6] font-bold text-lg font-mono">{match.score.home} - {match.score.away}</span>
                          ) : (
                            <span className="text-gray-500 text-sm">vs</span>
                          )}
                          <span className="text-white font-semibold text-sm truncate">{match.team_away}</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 ml-4">
                        {pred && (
                          <div className="text-right">
                            <div className={`text-lg font-bold font-mono ${confColor(pred.confidence)}`}>
                              {pred.confidence}%
                            </div>
                            <div className="text-xs text-gray-500">{predLabel(pred.prediction)}</div>
                          </div>
                        )}
                        {isPast && match.prediction_correct !== undefined && (
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${match.prediction_correct ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                            {match.prediction_correct ? <Target className="w-4 h-4 text-green-400" /> : <span className="text-red-400 text-xs font-bold">X</span>}
                          </div>
                        )}
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
                      </div>
                    </div>

                    {/* Expanded Analysis */}
                    {isExpanded && pred && (
                      <div className="mt-4 pt-4 border-t border-white/5 space-y-3" data-testid={`match-analysis-${idx}`}>
                        {/* Elo Ratings */}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-[#121212] p-3 rounded-lg">
                            <div className="text-xs text-gray-500 mb-1">{match.team_home} Elo</div>
                            <div className="text-white font-mono font-bold">{pred.home_elo}</div>
                          </div>
                          <div className="bg-[#121212] p-3 rounded-lg">
                            <div className="text-xs text-gray-500 mb-1">{match.team_away} Elo</div>
                            <div className="text-white font-mono font-bold">{pred.away_elo}</div>
                          </div>
                        </div>

                        {/* Probabilities */}
                        <div className="bg-[#121212] p-3 rounded-lg">
                          <div className="text-xs text-gray-500 mb-2">Wahrscheinlichkeiten</div>
                          <div className="grid grid-cols-3 gap-2 text-center">
                            <div>
                              <div className="text-xs text-gray-400">Heim</div>
                              <div className="text-white font-mono font-bold">{pred.probabilities?.home_win ? (pred.probabilities.home_win * 100).toFixed(0) : '-'}%</div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-400">Unentsch.</div>
                              <div className="text-white font-mono font-bold">{pred.probabilities?.draw ? (pred.probabilities.draw * 100).toFixed(0) : '-'}%</div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-400">Ausw.</div>
                              <div className="text-white font-mono font-bold">{pred.probabilities?.away_win ? (pred.probabilities.away_win * 100).toFixed(0) : '-'}%</div>
                            </div>
                          </div>
                        </div>

                        {/* Form */}
                        {pred.home_form && pred.home_form.length > 0 && (
                          <div className="bg-[#121212] p-3 rounded-lg">
                            <div className="text-xs text-gray-500 mb-2">Letzte Spiele</div>
                            <div className="flex justify-between">
                              <div>
                                <div className="text-xs text-gray-400 mb-1">{match.team_home}</div>
                                <div className="flex gap-1">
                                  {pred.home_form.map((r, i) => (
                                    <span key={i} className={`w-6 h-6 rounded text-xs font-bold flex items-center justify-center ${
                                      r === 'W' ? 'bg-green-500/20 text-green-400' : r === 'L' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'
                                    }`}>{r}</span>
                                  ))}
                                </div>
                              </div>
                              <div>
                                <div className="text-xs text-gray-400 mb-1">{match.team_away}</div>
                                <div className="flex gap-1">
                                  {(pred.away_form || []).map((r, i) => (
                                    <span key={i} className={`w-6 h-6 rounded text-xs font-bold flex items-center justify-center ${
                                      r === 'W' ? 'bg-green-500/20 text-green-400' : r === 'L' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'
                                    }`}>{r}</span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* H2H */}
                        <div className="flex items-center justify-between text-sm bg-[#121212] p-3 rounded-lg">
                          <span className="text-gray-400">Head-to-Head Daten</span>
                          <span className="text-white font-mono">{pred.h2h_matches} Spiele</span>
                        </div>

                        {/* Risk */}
                        <div className="flex items-center justify-between text-sm bg-[#121212] p-3 rounded-lg">
                          <span className="text-gray-400">Risiko</span>
                          <span className={`font-bold ${pred.risk === 'low' ? 'text-green-400' : pred.risk === 'medium' ? 'text-yellow-400' : 'text-red-400'}`}>
                            {pred.risk === 'low' ? 'Niedrig' : pred.risk === 'medium' ? 'Mittel' : 'Hoch'}
                          </span>
                        </div>

                        {/* Past match verdict */}
                        {isPast && match.prediction_correct !== undefined && (
                          <div className={`p-3 rounded-lg border ${match.prediction_correct ? 'border-green-500/30 bg-green-500/5' : 'border-red-500/30 bg-red-500/5'}`}>
                            <div className="text-xs text-gray-400 mb-1">KI-Analyse Rueckblick</div>
                            <div className={`text-sm ${match.prediction_correct ? 'text-green-400' : 'text-red-400'}`}>
                              {match.prediction_correct
                                ? `Korrekt vorhergesagt: ${predLabel(pred.prediction)} (${pred.confidence}% Konfidenz)`
                                : `Falsch: Vorhergesagt ${predLabel(pred.prediction)}, tatsaechlich ${predLabel(match.result)} (${match.score?.home}-${match.score?.away})`
                              }
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MatchSearchPage;
